import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountBalance,
  AccountBalanceWallet,
  Add,
  ArrowBackIosNew,
  AttachMoney,
  Check,
  CheckCircleOutline,
  CompareArrows,
  CreditCard,
  Delete,
  Edit,
  MoreHoriz,
  Paid,
  Receipt,
  ReceiptLong,
  Tune,
  Warning,
} from '@mui/icons-material';

const getManualTypeLabel = (value) => {
  const map = {
    shortage: 'Faltante',
    price_diff: 'Diferencia precio',
    pronto_pago: 'Pronto pago',
    plan_crecimiento: 'Plan crecimiento',
    promo_bonus: 'Bonificacion',
    negotiation: 'Negociacion',
  };
  return map[value] || value;
};

function CompactSuggestionRow({
  suggestion,
  selection,
  linkedNote,
  visibleAmount,
  isEarlyPayment,
  selectedEarlyOption,
  earlyOptions,
  onToggle,
  onBillToggle,
  onAmountChange,
  onOpenCreditNote,
  onOpenEarlyMenu,
  formatCurrency,
}) {
  const enabled = isEarlyPayment ? Boolean(selection?.enabled) : Boolean(selection?.enabled);
  const canBill = Boolean(suggestion.allowBill) && !isEarlyPayment;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 0.9,
        borderRadius: 1.25,
        borderColor: linkedNote ? 'secondary.main' : enabled ? 'primary.light' : 'divider',
        bgcolor: linkedNote ? 'secondary.50' : enabled ? 'primary.50' : 'background.paper',
      }}
    >
      <Stack spacing={0.65}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontSize: '0.76rem', fontWeight: 600 }}>
              {suggestion.title}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
              {suggestion.label}
            </Typography>
          </Box>
          <Chip
            size="small"
            color={linkedNote ? 'secondary' : enabled ? 'success' : 'default'}
            label={linkedNote ? `NC ${formatCurrency(linkedNote.amount ?? suggestion.amount)}` : formatCurrency(visibleAmount)}
            sx={{ height: 19, '& .MuiChip-label': { px: 0.7, fontSize: '0.64rem' } }}
          />
        </Stack>

        <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={enabled}
                disabled={Boolean(linkedNote)}
                onChange={(e) => onToggle?.(suggestion.key, e.target.checked)}
              />
            )}
            label={<Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Descontar</Typography>}
            sx={{ m: 0 }}
          />

          {canBill ? (
            <FormControlLabel
              control={(
                <Switch
                  size="small"
                  checked={Boolean(selection?.toBill)}
                  onChange={(e) => onBillToggle?.(suggestion.key, e.target.checked)}
                  disabled={!enabled || Boolean(linkedNote)}
                />
              )}
              label={<Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>Bill</Typography>}
              sx={{ m: 0 }}
            />
          ) : null}

          {Boolean(suggestion.editableAmount) ? (
            <TextField
              size="small"
              label="Monto"
              type="number"
              value={visibleAmount}
              onChange={(e) => onAmountChange?.(suggestion.key, e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
              sx={{ width: 116, '& .MuiInputBase-root': { fontSize: '0.75rem' } }}
              InputLabelProps={{ sx: { fontSize: '0.74rem' } }}
              disabled={Boolean(linkedNote)}
            />
          ) : null}

          {isEarlyPayment && earlyOptions.length > 1 ? (
            <Button
              size="small"
              variant="outlined"
              onClick={(event) => onOpenEarlyMenu?.(event, suggestion.key)}
              endIcon={<MoreHoriz fontSize="small" />}
              sx={{ textTransform: 'none', fontSize: '0.7rem', px: 1, height: 28 }}
              disabled={Boolean(linkedNote)}
            >
              {selectedEarlyOption
                ? `${selectedEarlyOption.percent}% ${selectedEarlyOption.period}d`
                : 'Plazo'}
            </Button>
          ) : null}

          <Button
            size="small"
            variant={linkedNote ? 'contained' : 'outlined'}
            color={linkedNote ? 'secondary' : 'inherit'}
            startIcon={<ReceiptLong fontSize="small" />}
            onClick={() => onOpenCreditNote?.(suggestion)}
            sx={{ textTransform: 'none', fontSize: '0.68rem', height: 28 }}
          >
            {linkedNote ? 'Editar NC' : 'NC'}
          </Button>
        </Stack>

        {suggestion.helper ? (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.66rem' }}>
            {suggestion.helper}
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  );
}

export default function PaymentDetailStep({
  showNotCompleted,
  showCompleteNeutral,
  hasCompensationOnly,
  totalPayments,
  hasNonCashPayments,
  movements,
  onOpenPaymentDocument,
  onOpenCreditNote,
  onOpenReceipt,
  onEditMovement,
  onDeleteMovement,
  shortageSuggestions,
  optionalAdjustmentSuggestions,
  suggestionSelections,
  earlyPaymentStateByKey,
  earlyPaymentBaseByKey,
  linkedSuggestionNoteMap,
  onSuggestionToggle,
  onSuggestionBillToggle,
  onSuggestionAmountChange,
  onOpenSuggestionCreditNote,
  onOpenEarlyPaymentMenu,
  manualAdjDraft,
  onManualDraftChange,
  onAddManualAdjustment,
  manualAdjustments,
  onRemoveManualAdjustment,
  selectedInvoices,
  selectableBills,
  loadingBills,
  billUsages,
  onBillUsageChange,
  billCreditsTotal,
  settlementComment,
  onSettlementCommentChange,
  canDistribute,
  distributionComplete,
  onOpenAllocate,
  onBack,
  onFinalize,
  finalizeGuard,
  payDocs,
  creditNotes,
  creditOnlyHasReceipt,
  onCreditOnlyHasReceiptChange,
  formatCurrency,
}) {
  const showMissingReceipt = movements.length > 0 && totalPayments > 0 && hasNonCashPayments;

  return (
    <Box sx={{ mt: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', mb: 0.2 }}>
            Detalle de pago
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
            Gestione aplicaciones y ajustes por factura en una vista compacta.
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.75}>
          {showNotCompleted ? (
            <Alert severity="warning" icon={<Warning fontSize="small" />} sx={{ py: 0, borderRadius: 1.5 }}>
              <Typography variant="caption" fontWeight={600}>Promocion pendiente</Typography>
            </Alert>
          ) : null}
          {showCompleteNeutral ? (
            <Alert severity="success" icon={<CheckCircleOutline fontSize="small" />} sx={{ py: 0, borderRadius: 1.5 }}>
              <Typography variant="caption" fontWeight={600}>Promocion completa</Typography>
            </Alert>
          ) : null}
        </Stack>
      </Stack>

      <Grid container spacing={1.5} alignItems="flex-start">
        <Grid item xs={12} lg={4}>
        <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5, height: '100%' }}>
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <AccountBalanceWallet sx={{ fontSize: 18, color: 'primary.main' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
                A) Aplicaciones y documentos de pago
              </Typography>
            </Stack>

            <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<Receipt sx={{ fontSize: 16 }} />}
                onClick={onOpenPaymentDocument}
                disabled={hasCompensationOnly}
                sx={{ textTransform: 'none', borderRadius: 1.25, fontSize: '0.75rem', height: 32 }}
              >
                Documento
              </Button>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<CreditCard sx={{ fontSize: 16 }} />}
                onClick={onOpenCreditNote}
                sx={{ textTransform: 'none', borderRadius: 1.25, fontSize: '0.75rem', height: 32 }}
              >
                N. Credito
              </Button>
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<AttachMoney sx={{ fontSize: 16 }} />}
                onClick={onOpenReceipt}
                sx={{ textTransform: 'none', borderRadius: 1.25, fontSize: '0.75rem', height: 32 }}
              >
                Recibo
              </Button>
            </Stack>

            {showMissingReceipt ? (
              <Alert severity="info" sx={{ py: 0.3, borderRadius: 1.25 }}>
                <Typography variant="caption" sx={{ fontSize: '0.68rem' }}>
                  Hay pagos sin recibo asociado.
                </Typography>
              </Alert>
            ) : null}

            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.69rem', fontWeight: 600 }}>
              APLICACIONES ({movements.length})
            </Typography>

            {movements.length === 0 ? (
              <Box sx={{ minHeight: 64, py: 0.8, border: '1px dashed', borderColor: 'divider', borderRadius: 1.25, textAlign: 'center', display: 'grid', placeItems: 'center' }}>
                <Box>
                <ReceiptLong sx={{ fontSize: 20, color: 'action.disabled' }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.73rem' }}>
                  Sin aplicaciones registradas
                </Typography>
                </Box>
              </Box>
            ) : (
              <Stack spacing={0.7} sx={{ maxHeight: { lg: 360 }, overflowY: { lg: 'auto' }, pr: { lg: 0.25 } }}>
                {movements.map((m) => (
                  <Card key={m.key} variant="outlined" sx={{ borderRadius: 1.25 }}>
                    <CardContent sx={{ py: 0.9, px: 1.1, '&:last-child': { pb: 0.9 } }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.76rem', fontWeight: 600 }}>
                            {m.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.67rem' }}>
                            {m.subtitle}
                          </Typography>
                        </Box>

                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Chip
                            size="small"
                            color="success"
                            label={`-${formatCurrency(m.amount)}`}
                            sx={{ height: 19, '& .MuiChip-label': { px: 0.7, fontSize: '0.64rem' } }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => onEditMovement?.(m)}
                            disabled={hasCompensationOnly && m.type === 'Pago'}
                            sx={{ width: 24, height: 24, color: 'primary.main' }}
                          >
                            <Edit sx={{ fontSize: 14 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => onDeleteMovement?.(m)}
                            sx={{ width: 24, height: 24, color: 'error.main' }}
                          >
                            <Delete sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Stack>
        </Paper>
        </Grid>

        <Grid item xs={12} lg={8}>
        <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
          <Stack spacing={1.2}>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Warning sx={{ fontSize: 18, color: 'warning.main' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
                B) Ajustes, faltantes y notas por factura
              </Typography>
            </Stack>

            <Grid container spacing={1.2}>
              <Grid item xs={12} lg={8}>
                <Stack spacing={1.1}>
                  <Paper variant="outlined" sx={{ p: 1, borderRadius: 1.5 }}>
                    <Stack spacing={0.8}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.69rem', fontWeight: 600 }}>
                        SUGERENCIAS DE AJUSTE
                      </Typography>

                      {shortageSuggestions.length === 0 && optionalAdjustmentSuggestions.length === 0 ? (
                        <Box sx={{ py: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1.1, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.73rem' }}>
                            Sin diferencias detectadas
                          </Typography>
                        </Box>
                      ) : (
                        <Stack spacing={0.7}>
                          {shortageSuggestions.map((suggestion) => {
                            const selection = suggestionSelections?.[suggestion.key] || {};
                            const linkedNote = linkedSuggestionNoteMap[suggestion.key];
                            return (
                              <CompactSuggestionRow
                                key={suggestion.key}
                                suggestion={suggestion}
                                selection={selection}
                                linkedNote={linkedNote}
                                visibleAmount={Number(selection.amount ?? suggestion.amount ?? 0)}
                                isEarlyPayment={false}
                                selectedEarlyOption={null}
                                earlyOptions={[]}
                                onToggle={onSuggestionToggle}
                                onBillToggle={onSuggestionBillToggle}
                                onAmountChange={onSuggestionAmountChange}
                                onOpenCreditNote={onOpenSuggestionCreditNote}
                                onOpenEarlyMenu={onOpenEarlyPaymentMenu}
                                formatCurrency={formatCurrency}
                              />
                            );
                          })}

                          {optionalAdjustmentSuggestions.map((suggestion) => {
                            const linkedNote = linkedSuggestionNoteMap[suggestion.key];
                            const isEarlyPayment = suggestion.type === 'pronto_pago';
                            const selection = isEarlyPayment
                              ? (earlyPaymentStateByKey[suggestion.key] || {})
                              : (suggestionSelections?.[suggestion.key] || {});
                            const earlyOptions = isEarlyPayment
                              ? (earlyPaymentBaseByKey[suggestion.key]?.options || [])
                              : [];
                            const selectedEarlyOption = isEarlyPayment
                              ? (earlyOptions.find((opt) => opt.optionKey === selection.selectedOptionKey) || earlyOptions[0] || null)
                              : null;
                            const visibleAmount = isEarlyPayment
                              ? Number(selection.amountEditable ?? suggestion.amount ?? 0)
                              : Number(selection.amount ?? suggestion.amount ?? 0);

                            return (
                              <CompactSuggestionRow
                                key={suggestion.key}
                                suggestion={suggestion}
                                selection={selection}
                                linkedNote={linkedNote}
                                visibleAmount={visibleAmount}
                                isEarlyPayment={isEarlyPayment}
                                selectedEarlyOption={selectedEarlyOption}
                                earlyOptions={earlyOptions}
                                onToggle={onSuggestionToggle}
                                onBillToggle={onSuggestionBillToggle}
                                onAmountChange={onSuggestionAmountChange}
                                onOpenCreditNote={onOpenSuggestionCreditNote}
                                onOpenEarlyMenu={onOpenEarlyPaymentMenu}
                                formatCurrency={formatCurrency}
                              />
                            );
                          })}
                        </Stack>
                      )}
                    </Stack>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 1, borderRadius: 1.5 }}>
                    <Stack spacing={0.9}>
                      <Stack direction="row" alignItems="center" spacing={0.6}>
                        <Tune sx={{ fontSize: 16, color: 'info.main' }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.69rem', fontWeight: 600 }}>
                          AJUSTE MANUAL
                        </Typography>
                      </Stack>

                      <Grid container spacing={0.8}>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth size="small">
                            <Select
                              value={manualAdjDraft.invoiceId || ''}
                              onChange={(e) => onManualDraftChange('invoiceId', Number(e.target.value))}
                              displayEmpty
                              sx={{ '& .MuiSelect-select': { py: 0.7, fontSize: '0.76rem' } }}
                            >
                              <MenuItem value="" disabled sx={{ fontSize: '0.75rem' }}>Factura</MenuItem>
                              {selectedInvoices.map((inv) => (
                                <MenuItem key={inv.id} value={inv.id} sx={{ fontSize: '0.75rem' }}>
                                  #{inv.number}{inv.series ? ` - ${inv.series}` : ''}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth size="small">
                            <Select
                              value={manualAdjDraft.type}
                              onChange={(e) => onManualDraftChange('type', e.target.value)}
                              sx={{ '& .MuiSelect-select': { py: 0.7, fontSize: '0.76rem' } }}
                            >
                              <MenuItem value="shortage" sx={{ fontSize: '0.75rem' }}>Faltante</MenuItem>
                              <MenuItem value="price_diff" sx={{ fontSize: '0.75rem' }}>Diferencia precio</MenuItem>
                              <MenuItem value="pronto_pago" sx={{ fontSize: '0.75rem' }}>Pronto pago</MenuItem>
                              <MenuItem value="plan_crecimiento" sx={{ fontSize: '0.75rem' }}>Plan crecimiento</MenuItem>
                              <MenuItem value="promo_bonus" sx={{ fontSize: '0.75rem' }}>Bonificacion</MenuItem>
                              <MenuItem value="negotiation" sx={{ fontSize: '0.75rem' }}>Negociacion</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Monto"
                            type="number"
                            value={manualAdjDraft.amount}
                            onChange={(e) => onManualDraftChange('amount', e.target.value)}
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ '& .MuiInputBase-input': { fontSize: '0.76rem' } }}
                            InputLabelProps={{ sx: { fontSize: '0.74rem' } }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Fecha efectiva"
                            type="date"
                            value={manualAdjDraft.effectiveDate || ''}
                            onChange={(e) => onManualDraftChange('effectiveDate', e.target.value)}
                            inputProps={{ max: new Date().toISOString().slice(0, 10) }}
                            InputLabelProps={{ shrink: true, sx: { fontSize: '0.74rem' } }}
                            sx={{ '& .MuiInputBase-input': { fontSize: '0.76rem' } }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Motivo"
                            value={manualAdjDraft.reason}
                            onChange={(e) => onManualDraftChange('reason', e.target.value)}
                            sx={{ '& .MuiInputBase-input': { fontSize: '0.76rem' } }}
                            InputLabelProps={{ sx: { fontSize: '0.74rem' } }}
                          />
                        </Grid>
                      </Grid>

                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={manualAdjDraft.toBill}
                              onChange={(e) => onManualDraftChange('toBill', e.target.checked)}
                            />
                          }
                          label={<Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.69rem' }}>Crear Bill</Typography>}
                          sx={{ m: 0 }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Add sx={{ fontSize: 14 }} />}
                          onClick={onAddManualAdjustment}
                          sx={{ textTransform: 'none', borderRadius: 1, fontSize: '0.7rem', height: 30 }}
                        >
                          Agregar ajuste
                        </Button>
                      </Stack>

                      {manualAdjustments.length > 0 ? (
                        <Stack spacing={0.55}>
                          <Divider />
                          {manualAdjustments.map((m, idx) => (
                            <Stack key={`madj-${idx}`} direction="row" justifyContent="space-between" alignItems="flex-start">
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontSize: '0.74rem', fontWeight: 600 }}>
                                  #{m.invoiceId} {getManualTypeLabel(m.type)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.66rem', display: 'block' }}>
                                  {m.reason || 'Sin motivo'}
                                </Typography>
                                <Typography variant="caption" color="success.main" sx={{ fontSize: '0.68rem', fontWeight: 600 }}>
                                  {formatCurrency(m.amount || 0)}{m.toBill ? ' (Bill)' : ''}
                                </Typography>
                              </Box>
                              <IconButton size="small" onClick={() => onRemoveManualAdjustment(idx)} sx={{ width: 22, height: 22 }}>
                                <Delete sx={{ fontSize: 13, color: 'error.main' }} />
                              </IconButton>
                            </Stack>
                          ))}
                        </Stack>
                      ) : null}
                    </Stack>
                  </Paper>
                </Stack>
              </Grid>

              <Grid item xs={12} lg={4}>
                <Stack spacing={1.1}>
                  <Paper variant="outlined" sx={{ p: 1, borderRadius: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={0.7} sx={{ mb: 0.8 }}>
                      <AccountBalance sx={{ fontSize: 16, color: 'error.main' }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.69rem', fontWeight: 600 }}>
                        SALDOS A FAVOR / BILLS
                      </Typography>
                    </Stack>

                    {loadingBills ? (
                      <Stack alignItems="center" py={1} spacing={0.5}>
                        <CircularProgress size={16} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.67rem' }}>
                          Cargando...
                        </Typography>
                      </Stack>
                    ) : selectableBills.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1.1 }}>
                        <Paid sx={{ fontSize: 20, color: 'success.main', opacity: 0.7 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.67rem' }}>
                          Sin saldos a favor
                        </Typography>
                      </Box>
                    ) : (
                      <Stack spacing={0.7}>
                        {selectableBills.map((bill) => {
                          const saldo = Number(bill.saldo_pendiente ?? bill.remaining_amount ?? 0);
                          const applied = billUsages[bill.id] || '';
                          return (
                            <Paper key={bill.id} variant="outlined" sx={{ p: 0.75, borderRadius: 1.1, bgcolor: 'error.50', borderColor: 'error.200' }}>
                              <Typography variant="caption" sx={{ fontSize: '0.68rem', fontWeight: 600, display: 'block' }}>
                                {bill.reason || 'Deuda'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.66rem', display: 'block', mb: 0.6 }}>
                                Disponible: {formatCurrency(saldo)}
                              </Typography>
                              <TextField
                                fullWidth
                                size="small"
                                label="Aplicar"
                                type="number"
                                value={applied}
                                onChange={(e) => onBillUsageChange(bill.id, e.target.value, saldo)}
                                inputProps={{ min: 0, max: saldo, step: 0.01 }}
                                sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
                                InputLabelProps={{ sx: { fontSize: '0.73rem' } }}
                              />
                            </Paper>
                          );
                        })}
                      </Stack>
                    )}

                    {billCreditsTotal > 0 ? (
                      <Stack direction="row" justifyContent="space-between" sx={{ mt: 1, pt: 0.8, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.67rem' }}>
                          Aplicado:
                        </Typography>
                        <Typography variant="caption" color="success.main" sx={{ fontSize: '0.69rem', fontWeight: 600 }}>
                          {formatCurrency(billCreditsTotal)}
                        </Typography>
                      </Stack>
                    ) : null}

                    <TextField
                      size="small"
                      multiline
                      rows={3}
                      fullWidth
                      label="Comentario"
                      value={settlementComment}
                      onChange={(e) => onSettlementCommentChange(e.target.value)}
                      sx={{ mt: 1, '& .MuiInputBase-input': { fontSize: '0.76rem' } }}
                      InputLabelProps={{ sx: { fontSize: '0.73rem' } }}
                    />
                  </Paper>

                  {canDistribute ? (
                    <Paper variant="outlined" sx={{ p: 1, borderRadius: 1.5, bgcolor: 'primary.50', borderColor: 'primary.light' }}>
                      <Stack spacing={0.8}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="caption" sx={{ fontSize: '0.69rem', fontWeight: 600 }}>
                            Distribucion pago parcial
                          </Typography>
                          <Chip
                            size="small"
                            color={distributionComplete ? 'success' : 'warning'}
                            label={distributionComplete ? 'Completa' : 'Pendiente'}
                            sx={{ height: 18, '& .MuiChip-label': { fontSize: '0.63rem' } }}
                          />
                        </Stack>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<CompareArrows sx={{ fontSize: 14 }} />}
                          onClick={onOpenAllocate}
                          sx={{ textTransform: 'none', fontSize: '0.72rem', height: 30 }}
                        >
                          Distribuir pago
                        </Button>
                      </Stack>
                    </Paper>
                  ) : null}
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </Paper>
        </Grid>
      </Grid>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          pt: 2,
          mt: 1.5,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1.25, sm: 0 },
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          size="small"
          onClick={onBack}
          startIcon={<ArrowBackIosNew sx={{ fontSize: 14 }} />}
          sx={{
            textTransform: 'none',
            borderRadius: 1.5,
            px: 2.25,
            fontSize: '0.8rem',
            height: 36,
            order: { xs: 2, sm: 1 },
          }}
        >
          Atras
        </Button>

        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ order: { xs: 1, sm: 2 }, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {(payDocs.length === 0 && creditNotes.length > 0) ? (
            <FormControlLabel
              control={
                <Switch size="small" checked={creditOnlyHasReceipt} onChange={(e) => onCreditOnlyHasReceiptChange(e.target.checked)} />
              }
              label={<Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.74rem' }}>Recibo ya emitido</Typography>}
              sx={{ mr: 0.5 }}
            />
          ) : null}

          <Tooltip title={finalizeGuard.ok ? '' : finalizeGuard.reason}>
            <span>
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<Check sx={{ fontSize: 15 }} />}
                disabled={!finalizeGuard.ok || selectedInvoices.length === 0}
                onClick={onFinalize}
                sx={{
                  textTransform: 'none',
                  borderRadius: 1.5,
                  px: 2.6,
                  fontSize: '0.8rem',
                  height: 36,
                  fontWeight: 600,
                }}
              >
                {hasCompensationOnly ? 'Finalizar compensacion' : 'Finalizar'}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Box>
    </Box>
  );
}
